// ExplainerCard — a reusable "how this works" guidance card. Expanded it shows an
// icon chip + title + body; it can be collapsed into a slim re-openable bar (not
// dismissed forever). State persists per `id` in localStorage so each explainer
// remembers its own collapsed/expanded choice. Use it anywhere a section needs a
// familiar, consistent inline explanation (Onboarding programs, etc.).
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { IconChip } from './IconChip';
import { NEXILINK_CARD_WHITE } from '@/lib/nexilink-card-styles';

interface ExplainerCardProps {
    /** Stable id — used as the localStorage key so this explainer remembers its state. */
    id: string;
    icon: LucideIcon;
    title: string;
    children: React.ReactNode;
    tone?: React.ComponentProps<typeof IconChip>['tone'];
    /** Collapsed on first view (before the user has toggled). Defaults to expanded. */
    defaultCollapsed?: boolean;
}

export const ExplainerCard: React.FC<ExplainerCardProps> = ({
    id,
    icon,
    title,
    children,
    tone = 'amber',
    defaultCollapsed = false,
}) => {
    const key = `nexilink-explainer-${id}`;
    const [collapsed, setCollapsed] = useState<boolean>(() => {
        if (typeof window === 'undefined') return defaultCollapsed;
        const v = window.localStorage.getItem(key);
        return v === null ? defaultCollapsed : v === '1';
    });

    const toggle = () => {
        setCollapsed((c) => {
            const next = !c;
            try { window.localStorage.setItem(key, next ? '1' : '0'); } catch { /* ignore */ }
            return next;
        });
    };

    if (collapsed) {
        // Deliberately understated — a faint, borderless ghost row so a dismissed
        // explainer recedes into the page instead of competing with real content.
        // Only reveals itself (and the "Visa" hint) on hover.
        const Icon = icon;
        return (
            <button
                onClick={toggle}
                className="group w-full flex items-center gap-2 px-1.5 py-1 text-left text-gray-400 hover:text-gray-600 transition-colors"
            >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2} />
                <span className="text-xs flex-1 truncate">{title}</span>
                <span className="text-xs flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    Visa <ChevronDown className="w-3.5 h-3.5" />
                </span>
            </button>
        );
    }

    return (
        <div className={`${NEXILINK_CARD_WHITE} p-5 relative`}>
            <button
                onClick={toggle}
                aria-label="Dölj"
                className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
                Dölj <ChevronUp className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-4">
                <IconChip icon={icon} size="lg" tone={tone} />
                <div className="min-w-0 pr-12">
                    <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                    <div className="mt-0.5">{children}</div>
                </div>
            </div>
        </div>
    );
};

export default ExplainerCard;
